import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Skeleton, Stack } from '@mui/material';
import MActionButtons from './MActionButtons';
import MAdaptativeMenu from '../MAdaptativeMenu';

import useMAdaptativeMenu from '../MAdaptativeMenu/useMAdaptativeMenu';

const CONTAINER_PADDING_X = 2;
const COLUMN_GAP = 1.5;
const BUTTON_WIDTH = 96;
const SKELETON_ITEMS_COUNT = 3;

/**
 * A responsive action bar that dynamically adjusts to screen width, displaying actions directly in the bar or within a dropdown menu as needed. Utilizes a combination of custom hooks for adaptability and responsiveness.
 *
 * @param {object} props - Component props.
 * @param {Array} props.actions - An array of action objects to be displayed within the action bar. Each action can include properties like `id`, `title`, `icon`, `handler`, and `divider`.
 * @param {boolean} [props.isLoading=false] - Controls the loading state of the action bar, displaying skeletons instead of action buttons when `true`.
 * @param {object} [props.sx] - Custom `sx` styling properties to apply to the action bar container.
 * @returns {JSX.Element} A responsive action bar that displays a set number of action buttons and groups additional actions in an adaptive menu.
 */
const MActionBar = ({ actions, isLoading, sx }) => {
  // Reference to the container DOM element to measure its width
  const containerRef = useRef(null);

  // State to manage visible and "more" action items
  const [[actionBarItems, moreItems], setTableaux] = useState([[], []]);

  // State to track the number of columns for rendering actions
  const [columns, setColumns] = useState(0);

  // State to handle the calculation/loading status of the action bar
  const [isCalculating, setIsCalculating] = useState(true);

  // Hook for managing the adaptive menu's open/close behavior
  const { open, handleOpen, handleClose } = useMAdaptativeMenu();

  // Reference to the "more options" button in the DOM
  const buttonMore = document.getElementById('more-options-button');

  // Memoized version of actions to avoid unnecessary re-renders caused by reference changes
  const stableActions = useMemo(() => actions, [JSON.stringify(actions)]);

  // Check if the component is ready to render actions (not calculating/loading)
  const isReady = !isCalculating && !isLoading;

  /**
   * Debounced function to handle resizing of the window or container.
   * It calculates how many actions can fit in the visible area and groups the rest into the "more" menu.
   */
  const handleRenderActions = useRef(
    debounce((data, loading) => {
      // Get the current width of the container element
      const containerWidth = containerRef?.current?.clientWidth;

      // Protect useless re-renders
      if (!data?.length || loading || !containerWidth) {
        setIsCalculating(false);
        return;
      }

      // Compute action bar items
      setIsCalculating(true);
      const columnWidth = COLUMN_GAP * 8;
      const maxItems = Math.floor(containerWidth / (BUTTON_WIDTH + columnWidth));
      getItems(data, maxItems, handleOpen, setTableaux);

      // Ensure smooth transition
      setTimeout(() => setIsCalculating(false), 250);
    }, 300)
  ).current;

  /**
   * Effect to initialize the the layout.
   */
  useEffect(() => {
    handleRenderActions(stableActions, isLoading);
  }, [stableActions, isLoading]);

  /**
   * Effect to manage the resize event listener.
   * Also calls the debounced resize handler initially to set up the layout.
   */
  useEffect(() => {
    window.addEventListener('resize', () => handleRenderActions(stableActions, isLoading));

    return () => {
      window.removeEventListener('resize', () => handleRenderActions(stableActions, isLoading));
      handleRenderActions.cancel();
    };
  }, [handleRenderActions, stableActions, isLoading]);

  /**
   * Effect to update the number of columns dynamically based on the visible items.
   * This ensures the grid layout adapts when the visible actions change.
   */
  useEffect(() => {
    const newColumns = actionBarItems.filter((item) => !item.divider).length ?? 0;
    setColumns(newColumns);
  }, [actionBarItems]);
  return (
    <>
      {open && (
        <MAdaptativeMenu
          anchorEl={buttonMore}
          open={open}
          options={moreItems}
          title="Options supplémentaires"
          handleClose={handleClose}
        />
      )}

      <Stack id="action-bar-main-container" ref={containerRef} sx={styleContainer(sx)}>
        <Stack
          id="action-bar-actions-container"
          sx={styleWrapper(isReady ? columns : SKELETON_ITEMS_COUNT, COLUMN_GAP, CONTAINER_PADDING_X)}
        >
          {isReady ? (
            <MActionButtons actions={actions} actionBarItems={actionBarItems} itemWidth={BUTTON_WIDTH} />
          ) : (
            [...Array(SKELETON_ITEMS_COUNT)].map((_, index) => (
              <Skeleton key={index} height={80} width={BUTTON_WIDTH} sx={{ alignSelf: 'center' }} />
            ))
          )}
        </Stack>
      </Stack>
    </>
  );
};

/**
 * Function to distribute actions into visible and "more" categories based on available space.
 *
 * @param {Array} actions - Array of all actions to be displayed.
 * @param {number} maxItems - Maximum number of actions that can fit in the visible area.
 * @param {Function} handleOpen - Function to handle opening the "more" menu.
 * @param {Function} setTableaux - State setter to update visible and "more" actions.
 */
function getItems(actions, maxItems, handleOpen, setTableaux) {
  // Dividers needs to be removed to compute only real action buttons
  const filteredActions = actions.filter((act) => !act.divider);

  // All actions go to the "more" menu if not enough space is available
  if (maxItems === 0) {
    setTableaux([[], actions]);
    return;
  }

  // Actions that fit in the visible area
  const visibleItems = filteredActions.slice(0, maxItems);

  // Remaining actions go to "more"
  const moreItems = filteredActions.slice(visibleItems.length - 1);

  if (filteredActions.length > visibleItems.length) {
    visibleItems.pop(); // Remove one visible action to make space for the "more options" button
    visibleItems.push({
      id: 'more-options-button',
      text: 'Plus',
      tooltip: "Afficher plus d'options",
      icon: 'more_horiz',
      handler: handleOpen // Opens the "more" menu
    });
  }

  // Update state with visible and "more" actions
  setTableaux([visibleItems, moreItems]);
}

const styleContainer = (sx) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignSelf: 'stretch', // ! required to prevent an infinite loop
  ...sx
});

const styleWrapper = (columns, columnGap, paddingX) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  columnGap,
  placeItems: 'center',
  alignItems: 'stretch',
  justifyItems: 'stretch',
  maxWidth: '100%',
  paddingX
});

MActionBar.propTypes = {
  actions: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  sx: PropTypes.object
};

export default MActionBar;
